'use client'

/**
 * PDP ACCORDION PL widget — the "product details" collapsible section (DETAILS / SIZE & FIT / CARE / …).
 *
 * ACCORDION is a pure container: each row is a nested `HTML` child widget (`data.htmlData = { title, text }`
 * + `config.defaultExpanded`). This widget flattens those children (see `accordion.adapter.ts`) and renders
 * one legacy `ProductDetailsTabWithReadMore` per row — reused read-only so the header/chevron, collapse
 * animation, read-more, HTML sanitization (`getSanitizedHtml`), and spacing match the non-PL section
 * exactly (it also picks DS vs non-DS internally, keeping parity with the rest of the page).
 *
 * Airline compatibility: the `accordionData.airlineCompatibilitySection` (present on luggage) is adapted
 * into the legacy `AirlineCompatibility` modal, opened by the `#airline-compatibility` link inside the
 * DETAILS HTML (via the shared `useHash` hook) — mirroring the non-PL ProductForm trigger.
 *
 * Renders nothing when there are no HTML children.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import { AccordionWidgetComponentDS } from '@src/components/blocks/accordion-widget-ds/accordion-widget-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetOfType } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { usePdpSelectionDerived } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { usePdpSectionImpressionRef, usePlProductProperties } from '@src/components/widgets/use-ia-impression'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { AccordionWidgetComponentLegacy } from './accordion-widget-legacy'
import { toAccordionItems, toSvdAccordionItems, withSvdVariantContent } from './accordion.adapter'

import type {
  AccordionWidgetData,
  ProductPurchaseV2WidgetData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Telemetry tokens — module-level consts (not inline literals) so they read as identifiers and stay
// clear of i18next/no-literal-string without suppression comments.
const DETAILS_IMPRESSION_ACTION = 'product_details_and_care'
const DETAILS_TRACKING_ID = 'product-description'
const DETAILS_CONTAINER_DISPLAY_NAME = 'Product Details, Size & Fit, Care'

// Hoisted so the object identity stays stable across renders — otherwise a fresh literal each render
// churns the scroll listener in `useTrackIsElementVisible` (a PDP re-renders on color/size selection).
const DETAILS_IMPRESSION_CONFIG = {
  action: DETAILS_IMPRESSION_ACTION,
  container: {
    contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.PRODUCT_DESCRIPTION,
    trackingId: DETAILS_TRACKING_ID,
    contentContainerDisplayName: DETAILS_CONTAINER_DISPLAY_NAME,
  },
}

// The DS vs legacy choice MUST be made per render, not at module scope: `shouldUseDesignSystem()`
// reads request-scoped module state on the server (set by `_app`) and the `<html data-styles>`
// attribute on the client. Calling it once at import time freezes `render` to whatever the value
// was at module-load (default `false` on the server, before any request), so SSR renders legacy
// while the client hydrates DS — a mismatch. Deciding inside the component re-evaluates it every
// render, keeping server and client in agreement.
function AccordionWidgetComponent(props: PLTypedWidgetProps<AccordionWidgetData>) {
  // IA-only `product_details_and_care` impression on scroll-into-view — mirrors the non-PL
  // ProductDetailsAndCare section (`entity_id: product_details_and_care`, `event_type: is_visible`),
  // including the `productProperties` built from the selected variant (non-PL parity).
  // Wrapper div carries the ref (matches the non-PL section's own wrapper), for both DS and legacy.
  const productProperties = usePlProductProperties('selected')
  const impressionRef = usePdpSectionImpressionRef(DETAILS_IMPRESSION_CONFIG, productProperties)

  // SVD content source — the sibling PRODUCT_PURCHASE_V2 payload, read cross-widget off the page-widget
  // store (the same pattern PRODUCT_PURCHASE uses to reach PRODUCT_SUMMARY's icons). This widget is served
  // ONLY on a V2/SVD payload, so on every non-SVD page this is `undefined` and the fallback below is
  // structurally unreachable — non-SVD rendering is byte-for-byte what it was.
  const svdSource = useWidgetOfType(WidgetType.PRODUCT_PURCHASE_V2)?.widget.data?.productPurchaseV2Data
  const { t } = useTranslation('common')

  // The resolved variant whose content these rows describe. DETAILS and CARE are variant-scoped in real
  // payloads (`attributeMap.variant`), so the rows must follow the shopper's selection — and this widget is
  // a SIBLING of the buy box, so it learns the selection the same way BRAND_COMPARISON does: off the
  // buy-box-published `selectedVariantId` (`usePdpSelectionDerived`, republished on every selection change).
  // Before the buy box publishes — SSR, first paint — fall back to the payload's own server-resolved
  // `selection.variantId`, which IS what the buy box seeds with, so the server and client frames agree and
  // the rows never flash product-level copy. `hydratedVariants` is keyed by variant id and holds the
  // per-variant `attributeValues`; an id with no entry simply yields `undefined` → product-level fallback.
  const selectedVariantId = usePdpSelectionDerived()?.selectedVariantId ?? svdSource?.selection?.variantId
  // `Partial<>` because proto3 OMITS an empty map: the generated type declares `hydratedVariants` as always
  // present, but a payload that hydrates nothing arrives without the key, and an id with no entry is normal
  // under lazy hydration. Both read as `undefined` here → product-level fallback, never a throw (the same
  // trap `anchorAttributes` hit, where an unguarded read blanked the whole widget via the error boundary).
  const hydratedVariants: Partial<ProductPurchaseV2WidgetData['hydratedVariants']> | undefined =
    svdSource?.hydratedVariants
  const svdVariant = selectedVariantId === undefined ? undefined : hydratedVariants?.[selectedVariantId]

  // Rows come from the ACCORDION's nested HTML children (V1 contract). A V2/SVD payload may send an EMPTY
  // container instead and carry the same copy as `attributeValues` on the purchase widget, so fall back to
  // those when there are no children.
  //
  // When children ARE present they keep owning the row SET (which rows, their order, titles and
  // `defaultExpanded`) — but NOT the body of a variant-scoped row. The HTML child's `text` is the server's
  // pre-render of the initially resolved variant, so on its own it freezes DETAILS / SHIPPING / CARE on the
  // first variant's copy for the whole session. `withSvdVariantContent` re-resolves each row against
  // `svdVariant`, which is why this branch is now selection-reactive too; see that function for the
  // precedence rule and why the static text stays the last fallback.
  const propsRecord = props as unknown as Record<string, unknown>
  const items = useMemo(() => {
    const htmlItems = toAccordionItems(propsRecord.widgets)
    return htmlItems.length > 0
      ? withSvdVariantContent(htmlItems, svdSource, svdVariant)
      : toSvdAccordionItems(svdSource, t, svdVariant)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsRecord.widgets, svdSource, svdVariant, t])

  // Both variants collapse to `null` when there are no rows; mirror that here so the impression
  // wrapper never renders (nor fires) for an empty section — the widget stays "renders nothing when empty".
  if (items.length === 0) return null

  const Component = shouldUseDesignSystem() ? AccordionWidgetComponentDS : AccordionWidgetComponentLegacy
  return (
    <div ref={impressionRef}>
      <Component {...props} items={items} />
    </div>
  )
}

export const AccordionWidget: PLTypedWidgetInterface<AccordionWidgetData, WidgetType.ACCORDION> = {
  id: WidgetType.ACCORDION,
  errorBoundary: PLBaseErrorBoundary,
  render: AccordionWidgetComponent,
}
